import { useCallback, useEffect, useMemo } from "react";

import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useGetLocalePath from "../hooks/useGetLocalePath";

import {
  setBlur,
  setContactReason,
  setContactZIndex,
  setShowContactDialog,
  setShowNewsletterRegisterDialog,
  setShowNewsletterUnregisterDialog,
} from "../redux/reducers/uiReducer";

import Media from "react-media";
import SEO from "../components/seo";
import LandingHeader from "../components/landing/landing-header";
import LandingBody from "../components/landing/landing-body";
import LandingFooter from "../components/landing/landing-footer";
import LandingIframe from "../components/landing/landing-iframe";

import {
  isEmpty,
  isEmptyObject,
  equalsIgnoringCase,
} from "../utils/validators";
import { logDebug } from "../utils/logger";

function Landing({ params }) {
  const { t, i18n } = useTranslation();

  const dispatch = useDispatch();
  const location = useLocation();
  const { getLocalePath } = useGetLocalePath();

  const titleSEO = useMemo(() => {
    const path = location.pathname;

    if (equalsIgnoringCase(path, getLocalePath(t("routes.public.about")))) {
      return `${t("app.name")} - ${t("landing.body.about_section.title")}`;
    } else if (
      equalsIgnoringCase(path, getLocalePath(t("routes.public.plans")))
    ) {
      return `${t("app.name")} - ${t("landing.body.plans_section.subtitle")}`;
    } else if (
      equalsIgnoringCase(path, getLocalePath(t("routes.public.contact")))
    ) {
      return `${t("app.name")} - ${t("contact.title")}`;
    } else if (
      equalsIgnoringCase(path, getLocalePath(t("routes.public.newsletter_reg")))
    ) {
      return `${t("app.name")} - ${t("reg_newsletter_dialog.title")}`;
    } else if (
      equalsIgnoringCase(
        path,
        getLocalePath(t("routes.public.newsletter_unreg"))
      )
    ) {
      return `${t("app.name")} - ${t("reg_newsletter_dialog.title")}`;
    } else {
      return `${t("app.name")} - ${t("landing.title")}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, location.pathname, t, getLocalePath]);

  const descriptionSEO = useMemo(() => {
    const path = location.pathname;

    if (equalsIgnoringCase(path, getLocalePath(t("routes.public.about")))) {
      return `${t("app.name")} - ${t(
        "landing.body.about_section.description"
      )}`;
    } else if (
      equalsIgnoringCase(path, getLocalePath(t("routes.public.plans")))
    ) {
      return `${t("app.name")} - ${t("landing.body.plans_section.subtitle")}`;
    } else if (
      equalsIgnoringCase(path, getLocalePath(t("routes.public.contact")))
    ) {
      return `${t("app.name")} - ${t("contact.description")}`;
    } else if (
      equalsIgnoringCase(path, getLocalePath(t("routes.public.newsletter_reg")))
    ) {
      return `${t("app.name")} - ${t("reg_newsletter_dialog.subtitle")}`;
    } else if (
      equalsIgnoringCase(
        path,
        getLocalePath(t("routes.public.newsletter_unreg"))
      )
    ) {
      return `${t("app.name")} - ${t("reg_newsletter_dialog.subtitle")}`;
    } else {
      return t("landing.description");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, location.pathname, t, getLocalePath]);

  const urlSEO = useMemo(() => {
    const publicURL = process.env.REACT_APP_PUBLIC_URL;
    const path = location.pathname;

    if (path.startsWith("/get/t/")) {
      return `${publicURL}${path}`;
    } else if (
      equalsIgnoringCase(path, getLocalePath(t("routes.public.about")))
    ) {
      return `${publicURL}${getLocalePath(
        getLocalePath(t("routes.public.about"))
      )}`;
    } else if (
      equalsIgnoringCase(path, getLocalePath(t("routes.public.plans")))
    ) {
      return `${publicURL}${getLocalePath(t("routes.public.plans"))}`;
    } else if (
      equalsIgnoringCase(path, getLocalePath(t("routes.public.contact")))
    ) {
      return `${publicURL}${getLocalePath(t("routes.public.contact"))}`;
    } else if (
      equalsIgnoringCase(path, getLocalePath(t("routes.public.newsletter_reg")))
    ) {
      return `${publicURL}${getLocalePath(t("routes.public.newsletter_reg"))}`;
    } else if (
      equalsIgnoringCase(
        path,
        getLocalePath(t("routes.public.newsletter_unreg"))
      )
    ) {
      return `${publicURL}${getLocalePath(
        t("routes.public.newsletter_unreg")
      )}`;
    } else {
      return `${publicURL}${getLocalePath(t("routes.common.home"))}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, location.pathname, t, getLocalePath]);

  const altLinksSEO = useMemo(() => {
    const publicURL = process.env.REACT_APP_PUBLIC_URL;
    let path = location.pathname;

    if (equalsIgnoringCase(path, getLocalePath(t("routes.public.about")))) {
      path = "routes.public.about";
    } else if (
      equalsIgnoringCase(path, getLocalePath(t("routes.public.plans")))
    ) {
      path = "routes.public.plans";
    } else if (
      equalsIgnoringCase(path, getLocalePath(t("routes.public.contact")))
    ) {
      path = "routes.public.contact";
    } else if (
      equalsIgnoringCase(path, getLocalePath(t("routes.public.newsletter_reg")))
    ) {
      path = "routes.public.newsletter_reg";
    } else if (
      equalsIgnoringCase(
        path,
        getLocalePath(t("routes.public.newsletter_unreg"))
      )
    ) {
      path = "routes.public.newsletter_unreg";
    } else {
      path = "";
    }

    const result = i18n.languages.map((lng) => {
      return {
        lng: lng,
        url: `${publicURL}/${lng}/${
          !isEmpty(path) ? t(path, { lng: lng }) : ""
        }`,
      };
    });

    const defaultLng = result.find((url) => url.lng === "en");
    result.push({
      lng: "x-default",
      url: defaultLng.url,
    });

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, i18n.languages, location.pathname, t, getLocalePath]);

  const targetId = useMemo(() => {
    return !isEmptyObject(params) && !isEmpty(params.targetId)
      ? params.targetId
      : !isEmptyObject(location?.state) && !isEmpty(location.state.targetId)
      ? location?.state.targetId
      : "";
  }, [location, params]);

  const showContactDialogHandler = useCallback(() => {
    logDebug("Landing", "showContactDialogHandler", {});
    dispatch(setContactReason("4"));
    dispatch(setContactZIndex(101));
    dispatch(setBlur(true));
    dispatch(setShowContactDialog(true));
  }, [dispatch]);

  const showNewsletterRegisterDialogHandler = useCallback(() => {
    logDebug("Landing", "showNewsletterRegisterDialogHandler", {});
    dispatch(setBlur(true));
    dispatch(setShowNewsletterRegisterDialog(true));
  }, [dispatch]);

  const showNewsletterUnregisterDialogHandler = useCallback(() => {
    logDebug("Landing", "showNewsletterUnregisterDialogHandler", {});
    dispatch(setBlur(true));
    dispatch(setShowNewsletterUnregisterDialog(true));
  }, [dispatch]);

  useEffect(() => {
    if (!isEmpty(targetId)) {
      if (equalsIgnoringCase(targetId, "contact")) {
        showContactDialogHandler();
      } else if (equalsIgnoringCase(targetId, "newsletter_register")) {
        showNewsletterRegisterDialogHandler();
      } else if (equalsIgnoringCase(targetId, "newsletter_unregister")) {
        showNewsletterUnregisterDialogHandler();
      } else {
        const el = document.getElementById(targetId);
        if (!isEmpty(el)) {
          el.scrollIntoView({
            block: "start",
            inline: "start",
            behavior: "smooth",
          });
        }
      }
    }
  }, [
    targetId,
    dispatch,
    showContactDialogHandler,
    showNewsletterRegisterDialogHandler,
    showNewsletterUnregisterDialogHandler,
  ]);

  return (
    <>
      <SEO
        title={titleSEO}
        description={descriptionSEO}
        url={urlSEO}
        canonical={urlSEO}
        altLinks={altLinksSEO}
      />
      <div id="landing" className="landing-container">
        <Media
          queries={{
            xs: "(min-width: 0px) and (max-width: 576px)",
            sm: "(min-width: 577px) and (max-width: 768px)",
            md: "(min-width: 769px) and (max-width: 992px)",
            lg: "(min-width: 993px) and (max-width: 1200px)",
            xl: "(min-width: 1201px) and (max-width: 1400px)",
            xxl: "(min-width: 1401px)",
          }}
        >
          {(matches) =>
            [matches.md, matches.lg, matches.xl, matches.xxl].some(
              (i) => i === true
            ) && <LandingIframe />
          }
        </Media>
        <LandingHeader />
        <LandingBody />
        <LandingFooter />
      </div>
    </>
  );
}

export default Landing;
